import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { householdApi, type Household, type HouseholdSummary } from '../api/household';

export const HOUSEHOLD_QUERY_KEY = ['household'];

export function useHousehold() {
  return useQuery<Household | null>({
    queryKey: HOUSEHOLD_QUERY_KEY,
    queryFn: async () => {
      try {
        return await householdApi.get();
      } catch (err: any) {
        // 404 = rota não encontrada; 403 NO_HOUSEHOLD = usuário ainda sem household
        // Ambos significam "sem household" → vai para onboarding
        if (err?.status === 404) return null;
        if (err?.status === 403 && err?.code === 'NO_HOUSEHOLD') return null;
        throw err;
      }
    },
    staleTime: 30_000,
    gcTime: 300_000,
  });
}

export function useCreateHousehold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: householdApi.create,
    onSuccess: (data) => {
      queryClient.setQueryData(HOUSEHOLD_QUERY_KEY, data);
    },
  });
}

export function useGenerateInvite() {
  return useMutation({
    mutationFn: householdApi.generateInvite,
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: householdApi.acceptInvite,
    onSuccess: (data) => {
      queryClient.setQueryData(HOUSEHOLD_QUERY_KEY, data);
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: householdApi.removeMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOUSEHOLD_QUERY_KEY });
    },
  });
}

export function useRenameHousehold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => householdApi.update({ name }),
    onSuccess: (data) => {
      queryClient.setQueryData(HOUSEHOLD_QUERY_KEY, data);
    },
  });
}

export function useUpdateMyName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => householdApi.updateMyName(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOUSEHOLD_QUERY_KEY });
    },
  });
}

export function useHouseholds() {
  return useQuery<HouseholdSummary[]>({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        const res = await householdApi.list();
        return res.households;
      } catch {
        return [];
      }
    },
    staleTime: 60_000,
    gcTime: 300_000,
  });
}
